import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "store/features/auth/authSlice";
import { toast } from "sonner";
import Breadcrumb from "components/Common/Breadcrumb";
import DashboardLoader from "components/Loader/DashboardLoader";
import {
  useGetProjectByIdQuery,
  useGetClientQuery,
} from "store/features/client/clientManagementApi";
import logoImg from "../../assets/images/logo-sm.png";
import { useCreateInvoiceMutation } from "store/features/invoice/invoiceApi";

const ProjectInvoice = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const invoiceRef = useRef(null);

  const { data: project, isLoading: projectLoading } = useGetProjectByIdQuery(projectId);
  const { data: client, isLoading: clientLoading } = useGetClientQuery(project?.client, {
    skip: !project?.client,
  });

  const [isSending, setIsSending] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [createInvoice] = useCreateInvoiceMutation();

  // Add after existing useState declarations
  const [currency, setCurrency] = useState("BDT");
  const [paymentToCollect, setPaymentToCollect] = useState(0);

  // Editable invoice state
  const [invoiceData, setInvoiceData] = useState({
    // Company Info (Left side - Qwik IT Services)
    companyHeadOffice: "950 Hillcrest Rd, Saint John, NB E2N2B2",
    companyBangladeshOffice: "House-752 Rd-8, Adabor, Dhaka 1207",
    companySupportEmail: "support@qwikit.ca",
    companyBillingEmail: "billing@qwikit.ca",
    companyPhoneBD: "+880 1636-131313",
    companyPhoneCanada: "+1 (506) 899-3939",
    companyWebsite: "www.qwikit.ca",

    // Bill To (Client Info)
    billToCompany: "",
    billToAddress: "",
    billToCountry: "Bangladesh",
    billToPhone: "",
    billToEmail: "",

    // Invoice Details
    invoiceNo: "",
    invoiceDate: new Date().toISOString().split("T")[0],
    dueDate: "",

    // Payment Methods
    bankName: "City Bank PLC - Dhanmondi Branch",
    bankAccount: "125 492 423 8001",
    bankRouting: "225261187",
    bankSwift: "CIBLBDDH",
    bkashNumber: "01680001000",
    stripeLink: "https://buy.stripe.com/00g17Be75cU3fWE3cd",
    interacEmail: "",

    // Footer
    footerText: "Thank you for your business with Qwik IT Services",
  });

  // Project items (editable)
  const [projectItems, setProjectItems] = useState([
    {
      id: 1,
      projectName: "",
      description: "",
      amount: 0,
    },
  ]);

  // Calculations
  const [calculations, setCalculations] = useState({
    total: 0,
    received: 0,
    outstanding: 0,
  });

  // Load project data into invoice
  useEffect(() => {
    if (project && client) {
      // Calculate total from project budget
      const projectBudget = parseFloat(project.budget) || 0;

      // Calculate total received from payments
      const totalReceived = (project.payment || []).reduce(
        (sum, payment) => sum + (parseFloat(payment.amount) || 0),
        0
      );

      const outstandingAmount = projectBudget - totalReceived;

      // Set initial project items from project data
      const items = [
        {
          id: 1,
          projectName: project.name || "",
          description: project.description || "",
          amount: projectBudget,
        },
      ];

      setProjectItems(items);

      // Set calculations
      setCalculations({
        total: projectBudget,
        received: totalReceived,
        outstanding: outstandingAmount,
      });

      setPaymentToCollect(outstandingAmount);


      // Get client email from client data
      const clientEmail = client.client_email || client.email || "";

      // Set invoice data with client info
      setInvoiceData((prev) => ({
        ...prev,
        // Bill To (Client Info)
        billToCompany: client.company_name || client.client_name || "",
        billToAddress: client.address || "",
        billToPhone: client.contact_number || "",
        billToCountry: "Bangladesh",
        billToEmail: clientEmail,

        // Invoice details
        invoiceNo: `QIT-INV-${project.id}-${Date.now().toString().slice(-6)}`,
        invoiceDate: new Date().toISOString().split("T")[0],
        dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
      }));
    }
  }, [project, client]);

  // Update calculations when items change
  useEffect(() => {
    const total = projectItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const received = calculations.received;
    const outstanding = total - received;

    setCalculations((prev) => ({
      ...prev,
      total: total,
      outstanding: outstanding,
    }));

    setPaymentToCollect(outstanding);


  }, [projectItems]);

  // Format currency
  const formatCurrency = (amount) => {
    return `BDT ${Math.round(amount).toLocaleString()}`;
  };


  // Format currency with selected currency code
  // const formatCurrencyWithCode = (amount) => {
  //   const formattedAmount = Math.round(amount).toLocaleString();

  //   switch (currency) {
  //     case "USD":
  //       return `USD ${formattedAmount}`;
  //     case "CAD":
  //       return `CAD ${formattedAmount}`;
  //     case "EURO":
  //       return `EURO ${formattedAmount}`;
  //     case "BDT":
  //       return `BDT ${formattedAmount}`;
  //     default:
  //       return `BDT ${formattedAmount}`;
  //   }
  // };

  // Format currency with selected currency code - FIXED for decimal numbers
  const formatCurrencyWithCode = (amount) => {
    // Handle undefined, null, or invalid values
    if (amount === undefined || amount === null || isNaN(amount)) {
      amount = 0;
    }

    // Format with 2 decimal places for proper currency display
    const formattedAmount = Number(amount).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

    switch (currency) {
      case "USD":
        return `USD ${formattedAmount}`;
      case "CAD":
        return `CAD ${formattedAmount}`;
      case "EURO":
        return `EURO ${formattedAmount}`;
      case "BDT":
        return `BDT ${formattedAmount}`;
      default:
        return `BDT ${formattedAmount}`;
    }
  };


  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Handle invoice field changes
  const handleInvoiceChange = (field, value) => {
    setInvoiceData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle project item changes
  const handleProjectItemChange = (id, field, value) => {
    setProjectItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, [field]: field === "amount" ? parseFloat(value) || 0 : value } : item
      )
    );
  };

  // Add new project item
  const addProjectItem = () => {
    const newId = Math.max(...projectItems.map((i) => i.id), 0) + 1;
    setProjectItems([
      ...projectItems,
      {
        id: newId,
        projectName: "",
        description: "",
        amount: 0,
      },
    ]);
  };

  // Remove project item
  const removeProjectItem = (id) => {
    if (projectItems.length > 1) {
      setProjectItems(projectItems.filter((item) => item.id !== id));
    } else {
      toast.error("At least one project item is required");
    }
  };

  // Handle received amount change
  const handleReceivedChange = (value) => {
    const received = parseFloat(value) || 0;
    setCalculations((prev) => ({
      ...prev,
      received: received,
      outstanding: prev.total - received,
    }));

    setPaymentToCollect(newOutstanding);

  };

  // Generate responsive email HTML
  const generateEmailHTML = () => {
    const itemsRows = projectItems
      .map(
        (item) => `
      <tr>
        <td style="padding:12px;border-bottom:1px solid #eee;">${item.projectName || "-"}</td>
        <td style="padding:12px;border-bottom:1px solid #eee;">${item.description || "-"}</td>
        <td style="padding:12px;border-bottom:1px solid #eee;text-align:right;">${formatCurrencyWithCode(item.amount)}</td>
      </tr>
    `
      )
      .join("");

    return `
<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>Invoice</title>

 <style>
 @media only screen and (max-width:600px) {
  .stack-column {
    display: block !important;
    width: 100% !important;
    padding: 0 !important;
  }

   .stack-center {
    display: block !important;
    width: 100% !important;
    text-align: center !important;
    padding: 0 !important;
    margin-bottom: 10px;
  }

  .stack-left {
    display: block !important;
    width: 100% !important;
    text-align: left !important;
    padding-top: 10px !important;
  }

}
  </style>
</head>

<body style="margin:0;padding:0;background:#eef2f7;font-family:Arial, sans-serif;">

<table width="100%" cellpadding="0" cellspacing="0" style="padding:16px;">
<tr>
<td align="center">

<table width="100%" cellpadding="0" cellspacing="0" style="max-width:900px;background:#fff;border-radius:16px;overflow:hidden;">

<!-- HEADER -->
<!-- HEADER -->
<tr>
<td style="background:linear-gradient(135deg,#1e3a8a,#2563eb);color:white;padding:20px;">

  <table width="100%" cellpadding="0" cellspacing="0">
    <tr>

      <!-- LEFT (LOGO) -->
      <td class="stack-center" width="80" valign="middle" align="center">
  <img 
    src="https://res.cloudinary.com/dlaeldpqy/image/upload/v1777306544/logo-sm_ubs6bx.png"
    width="60"
    style="display:block; margin:0 auto;">
</td>

      <!-- MIDDLE (TITLE) -->
      <td class="stack-center" valign="middle" style="padding-left:10px;">
        <h1 style="margin:0;font-size:24px;">INVOICE</h1>
        <p style="margin:3px 0;font-size:12px;">Qwik IT Services</p>
      </td>

      <!-- RIGHT (INFO) -->
      <td class="stack-left" align="right" valign="middle" style="font-size:12px;">
        <p><strong>Invoice No:</strong> ${invoiceData.invoiceNo}</p>
        <p><strong>Date:</strong> ${formatDate(invoiceData.invoiceDate)}</p>
      </td>

    </tr>
  </table>

</td>
</tr>

<!-- CONTENT -->
<tr>
<td style="padding:20px;">

<!-- INFO GRID -->
<!-- INFO GRID -->
<table width="100%" cellpadding="0" cellspacing="0">
  <tr>

    <!-- LEFT -->
    <td class="stack-column" width="50%" valign="top" style="padding-right:10px; margin-bottom:8px">
      <table width="100%" style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:12px;">
        <tr>
          <td style="padding:16px;">
            <h3 style="margin:0 0 10px;color:#1e3a8a;">Bill To</h3>
            <p><strong>${invoiceData.billToCompany || "-"}</strong></p>
            <p>${invoiceData.billToAddress || "-"}</p>
            <p>${invoiceData.billToCountry || "-"}</p>
            <p>${invoiceData.billToPhone || "-"}</p>
            <p>${invoiceData.billToEmail || "-"}</p>
          </td>
        </tr>
      </table>
    </td>

    <!-- RIGHT -->
    <td class="stack-column" width="50%" valign="top" style="padding-left:10px;">
      <table width="100%" style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:12px;">
        <tr>
          <td style="padding:16px;">
            <h3 style="margin:0 0 10px;color:#1e3a8a;">Qwik IT Services</h3>
            <p><strong>Head Office:</strong> ${invoiceData.companyHeadOffice}</p>
            <p><strong>Bangladesh Office:</strong> ${invoiceData.companyBangladeshOffice}</p>
            <p><strong>Phone:</strong> ${invoiceData.companyPhoneBD}, ${invoiceData.companyPhoneCanada}</p>
            <p><strong>Support:</strong> ${invoiceData.companySupportEmail}</p>
            <p><strong>Billing:</strong> ${invoiceData.companyBillingEmail}</p>
          </td>
        </tr>
      </table>
    </td>

  </tr>
</table>

<!-- TABLE -->
<table width="100%" cellpadding="0" cellspacing="0" style="margin-top:20px;border-collapse:collapse;">
<tr style="background:#1e3a8a;color:white;">
<th style="padding:10px;text-align:left;">Project</th>
<th style="padding:10px;text-align:left;">Description</th>
<th style="padding:10px;text-align:right;">Amount</th>
</tr>

${itemsRows}

<tr style="background:#f9fafb;font-weight:bold;">
<td colspan="2" style="padding:10px;">Total</td>
<td style="padding:10px;text-align:right;">${formatCurrencyWithCode(calculations.total)}</td>
</tr>
</table>

<!-- SUMMARY -->
<table width="100%" style="margin-top:20px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:12px;">
<tr>
<td style="padding:10px;">Total</td>
<td align="right" style="padding:10px;">${formatCurrencyWithCode(calculations.total)}</td>
</tr>
<tr>
<td style="padding:10px;">Received</td>
<td align="right" style="padding:10px;color:green;">${formatCurrencyWithCode(calculations.received)}</td>
</tr>
<tr>
<td style="padding:10px;">Outstanding</td>
<td align="right" style="padding:10px;color:red;">${formatCurrencyWithCode(calculations.outstanding)}</td>
</tr>
</table>

<!-- PAYMENT -->
<table width="100%" style="margin-top:20px;background:#fff7ed;border-left:4px solid #f97316;text-align:center;">
<tr>
<td style="padding:16px;">
<p>Minimum Due by: ${formatDate(invoiceData.dueDate)}</p>
<h2 style="color:red;">${formatCurrencyWithCode(paymentToCollect)}</h2>
</td>
</tr>
</table>

<!-- METHODS -->
<h3 style="margin-top:20px;color:#1e3a8a;">Payment Methods</h3>

<table width="100%" style="margin-top:10px;background:#f8fafc;border:1px solid #e5e7eb;border-radius:12px;">
<tr><td style="padding:12px;">
<p>

<strong>🏦 Bank Transfer</strong><br>
</p>
<p>
${invoiceData.bankName}
</p>
<p>

Account: ${invoiceData.bankAccount}<br>
</p>
<p>

Routing: ${invoiceData.bankRouting}<br>
</p>
<p>

SWIFT: ${invoiceData.bankSwift}
</p>
</td>
</tr>
</table>

<table width="100%" style="margin-top:10px;background:#f8fafc;border:1px solid #e5e7eb;border-radius:12px;">
<tr><td style="padding:12px;">

<p>

<strong>📱 bKash</strong><br>
</p>
<p>

${invoiceData.bkashNumber}<br>
</p>
<p>

Send transaction ID after payment
</p>
</td></tr>
</table>

<table width="100%" style="margin-top:10px;background:#f8fafc;border:1px solid #e5e7eb;border-radius:12px;">
<tr><td style="padding:12px;">
<p>

<strong>💳 Stripe</strong><br>
</p>
<p>

${invoiceData.stripeLink}
</p>
</td></tr>
</table>

${invoiceData.interacEmail && invoiceData.interacEmail.trim() !== ""
        ? `
<table width="100%" style="margin-top:10px;background:#f8fafc;border:1px solid #e5e7eb;border-radius:12px;">
<td style="padding:12px;">
<p><strong>🏦 Interac e-Transfer</strong></p>
<p>Send payment to: <strong>${invoiceData.interacEmail}</strong></p>
</td>
</table>
`
        : ""
      }


<!-- SIGNATURE -->
<div style="margin-top:20px;text-align:center;font-size:13px;">
<p>Best regards,</p>
<p><strong>Qwik IT Services</strong></p>
<p>${invoiceData.companyPhoneCanada}</p>
<p>${invoiceData.companyWebsite}</p>
</div>

</td>
</tr>

<!-- FOOTER -->
<tr>
<td style="text-align:center;padding:20px;background:#f3f4f6;font-size:12px;">
${invoiceData.footerText}
</td>
</tr>

</table>

</td>
</tr>
</table>

</body>
</html>
`;
  };

  // Function to send email only
  const sendEmailOnly = async (recipientEmail) => {
    const emailHTML = generateEmailHTML();

    const response = await fetch('https://resend-server.qwikit.ca/api/send-invoice', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to: recipientEmail,
        subject: `Invoice #${invoiceData.invoiceNo} - Qwik IT Services`,
        html: emailHTML,
        invoiceNo: invoiceData.invoiceNo,
        clientName: invoiceData.billToCompany,
      }),
    });

    return response.json();
  };

  // Function to save data to database
  const saveToDatabase = async (recipientEmail) => {
    const saveData = {
      project: parseInt(projectId),
      user: user?.id,
      invoice_no: invoiceData.invoiceNo,
      invoice_data: {
        companyInfo: {
          headOffice: invoiceData.companyHeadOffice,
          bangladeshOffice: invoiceData.companyBangladeshOffice,
          supportEmail: invoiceData.companySupportEmail,
          billingEmail: invoiceData.companyBillingEmail,
          phoneBD: invoiceData.companyPhoneBD,
          phoneCanada: invoiceData.companyPhoneCanada,
          website: invoiceData.companyWebsite
        },
        clientInfo: {
          companyName: invoiceData.billToCompany,
          address: invoiceData.billToAddress,
          country: invoiceData.billToCountry,
          phone: invoiceData.billToPhone,
          email: invoiceData.billToEmail
        },
        invoiceDetails: {
          invoiceNo: invoiceData.invoiceNo,
          invoiceDate: invoiceData.invoiceDate,
          dueDate: invoiceData.dueDate
        },
        paymentMethods: {
          bankName: invoiceData.bankName,
          bankAccount: invoiceData.bankAccount,
          bankRouting: invoiceData.bankRouting,
          bankSwift: invoiceData.bankSwift,
          bkashNumber: invoiceData.bkashNumber,
          stripeLink: invoiceData.stripeLink,
          interacEmail: invoiceData.interacEmail
        },
        footerText: invoiceData.footerText
      },
      project_items: projectItems,
      // calculations: calculations,
      calculations: {
        total: calculations.total,
        received: calculations.received,
        outstanding: calculations.outstanding,
        payment_to_collect: paymentToCollect,
        currency: currency
      },
      sent_to: recipientEmail,
      sent_at: new Date().toISOString(),
    };

    const result = await createInvoice(saveData).unwrap();
    return result;
  };

  // Handle Send Only
  const handleSendOnly = async () => {
    if (!invoiceData.billToCompany) {
      toast.error("Please enter client company name");
      return;
    }

    const recipientEmail = invoiceData.billToEmail || client?.client_email;

    if (!recipientEmail) {
      toast.error("Client email is required to send invoice");
      return;
    }

    try {
      setIsSending(true);

      const emailData = await sendEmailOnly(recipientEmail);

      if (emailData.success) {
        toast.success(`Invoice #${invoiceData.invoiceNo} sent successfully to ${recipientEmail}`);
        setShowModal(false);
      } else {
        throw new Error(emailData.error || 'Failed to send invoice');
      }
    } catch (error) {
      console.error('Error sending invoice:', error);
      toast.error('Failed to send invoice. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  // Handle Save & Send
  const handleSaveAndSend = async () => {
    if (!invoiceData.billToCompany) {
      toast.error("Please enter client company name");
      return;
    }

    const recipientEmail = invoiceData.billToEmail || client?.client_email;

    if (!recipientEmail) {
      toast.error("Client email is required to send invoice");
      return;
    }

    try {
      setIsSending(true);

      // Step 1: Send email
      const emailData = await sendEmailOnly(recipientEmail);

      if (!emailData.success) {
        throw new Error(emailData.error || 'Failed to send invoice');
      }

      // Step 2: Save to database
      const saveResult = await saveToDatabase(recipientEmail);

      if (saveResult.id) {
        toast.success(`Invoice #${invoiceData.invoiceNo} sent and saved! ID: ${saveResult.id}`);
        setShowModal(false);
      } else {
        toast.warning('Email sent but failed to save to database');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to send invoice. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  // Open modal before sending
  const openSendModal = () => {
    if (!invoiceData.billToCompany) {
      toast.error("Please enter client company name");
      return;
    }

    const recipientEmail = invoiceData.billToEmail || client?.client_email;

    if (!recipientEmail) {
      toast.error("Client email is required to send invoice");
      return;
    }

    setShowModal(true);
  };

  if (projectLoading || clientLoading) {
    return <DashboardLoader />;
  }

  if (!project) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <div className="alert alert-danger">Project not found</div>
          <Link to="/all-projects" className="btn btn-primary">
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Invoice" breadcrumbItem={`Invoice for ${project?.name || "Project"}`} />

        {/* Action Buttons */}
        <div className="row mb-4 no-print">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <Link to="/all-projects" className="btn btn-secondary">
                <i className="fas fa-arrow-left me-1"></i> Back to Projects
              </Link>
              <button
                type="button"
                className="btn btn-success"
                onClick={openSendModal}
                disabled={isSending}
              >
                <i className="fas fa-envelope me-1"></i>
                {isSending ? "Processing..." : "Send Invoice to Client"}
              </button>
            </div>
          </div>
        </div>

        {/* Edit Form Section - Editable Fields */}
        <div className="row mb-4 no-print">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title mb-3">Edit Invoice Details</h5>

                <div className="row">
                  {/* Bill To Section */}
                  <div className="col-md-6">
                    <h6 className="mb-2 text-primary">Bill To (Client Information)</h6>
                    <div className="mb-2">
                      <label className="form-label small">Company Name</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={invoiceData.billToCompany}
                        onChange={(e) => handleInvoiceChange("billToCompany", e.target.value)}
                      />
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Address</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={invoiceData.billToAddress}
                        onChange={(e) => handleInvoiceChange("billToAddress", e.target.value)}
                      />
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Country</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={invoiceData.billToCountry}
                        onChange={(e) => handleInvoiceChange("billToCountry", e.target.value)}
                      />
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Phone</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={invoiceData.billToPhone}
                        onChange={(e) => handleInvoiceChange("billToPhone", e.target.value)}
                      />
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Email</label>
                      <input
                        type="email"
                        className="form-control form-control-sm"
                        value={invoiceData.billToEmail}
                        onChange={(e) => handleInvoiceChange("billToEmail", e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Invoice Details */}
                  <div className="col-md-6">
                    <h6 className="mb-2 text-primary">Invoice Details</h6>
                    <div className="mb-2">
                      <label className="form-label small">Invoice No</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={invoiceData.invoiceNo}
                        onChange={(e) => handleInvoiceChange("invoiceNo", e.target.value)}
                      />
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Invoice Date</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={invoiceData.invoiceDate}
                        onChange={(e) => handleInvoiceChange("invoiceDate", e.target.value)}
                      />
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Due Date</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={invoiceData.dueDate}
                        onChange={(e) => handleInvoiceChange("dueDate", e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <hr />

                {/* Project Items */}
                <h6 className="mb-2 text-primary">Project Items</h6>
                <div className="table-responsive">
                  <table className="table table-bordered table-sm">
                    <thead>
                      <tr>
                        <th>Project Name</th>
                        <th>Description</th>
                        <th style={{ width: "150px" }}>Amount</th>
                        <th style={{ width: "50px" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {projectItems.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={item.projectName}
                              onChange={(e) => handleProjectItemChange(item.id, "projectName", e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={item.description}
                              onChange={(e) => handleProjectItemChange(item.id, "description", e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={item.amount}
                              onChange={(e) => handleProjectItemChange(item.id, "amount", e.target.value)}
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => removeProjectItem(item.id)}
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-2">
                  <button type="button" className="btn btn-sm btn-primary" onClick={addProjectItem}>
                    <i className="fas fa-plus me-1"></i> Add Item
                  </button>
                  <div className="text-end">
                    <strong>Total: {formatCurrencyWithCode(calculations.total)}</strong>
                  </div>
                </div>

                <hr />

                {/* Payment Section */}
                <div className="row">
                  <div className="col-md-4">
                    <h6 className="mb-2 text-primary">Received Amount</h6>
                    <input
                      type="number"
                      className="form-control"
                      value={calculations.received}
                      onChange={(e) => handleReceivedChange(e.target.value)}
                      step="0.01"
                    />
                    <small className="text-muted">Amount already received from client</small>
                  </div>
                  <div className="col-md-4">
                    <h6 className="mb-2 text-primary">Outstanding Amount</h6>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={formatCurrencyWithCode(calculations.outstanding)}
                      readOnly
                    />
                  </div>
                </div>

                {/* Add after the Received Amount section */}
                <div className="row mt-3">
                  <div className="col-md-4">
                    <h6 className="mb-2 text-primary">Currency</h6>
                    <select
                      className="form-select"
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                    >
                      <option value="BDT">BDT</option>
                      <option value="USD">USD</option>
                      <option value="CAD">CAD</option>
                      <option value="EURO">EURO</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <h6 className="mb-2 text-primary">Payment to Collect</h6>
                    <input
                      type="number"
                      className="form-control"
                      value={paymentToCollect}
                      onChange={(e) => setPaymentToCollect(parseFloat(e.target.value) || 0)}
                      step="0.01"
                    />
                    <small className="text-muted">Amount client should pay now (defaults to outstanding)</small>
                  </div>
                </div>
                <hr />

                {/* Company Info */}
                <h6 className="mb-2 text-primary">Company Information (Qwik IT Services)</h6>
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-2">
                      <label className="form-label small">Head Office (Canada)</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={invoiceData.companyHeadOffice}
                        onChange={(e) => handleInvoiceChange("companyHeadOffice", e.target.value)}
                      />
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Bangladesh Office</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={invoiceData.companyBangladeshOffice}
                        onChange={(e) => handleInvoiceChange("companyBangladeshOffice", e.target.value)}
                      />
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Support Email</label>
                      <input
                        type="email"
                        className="form-control form-control-sm"
                        value={invoiceData.companySupportEmail}
                        onChange={(e) => handleInvoiceChange("companySupportEmail", e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-2">
                      <label className="form-label small">Billing Email</label>
                      <input
                        type="email"
                        className="form-control form-control-sm"
                        value={invoiceData.companyBillingEmail}
                        onChange={(e) => handleInvoiceChange("companyBillingEmail", e.target.value)}
                      />
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Phone (BD)</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={invoiceData.companyPhoneBD}
                        onChange={(e) => handleInvoiceChange("companyPhoneBD", e.target.value)}
                      />
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Phone (Canada)</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={invoiceData.companyPhoneCanada}
                        onChange={(e) => handleInvoiceChange("companyPhoneCanada", e.target.value)}
                      />
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Website</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={invoiceData.companyWebsite}
                        onChange={(e) => handleInvoiceChange("companyWebsite", e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <hr />

                {/* Payment Methods */}
                <h6 className="mb-2 text-primary">Payment Methods</h6>
                <div className="row">
                  <div className="col-md-4">
                    <label className="form-label small">Bank Name</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={invoiceData.bankName}
                      onChange={(e) => handleInvoiceChange("bankName", e.target.value)}
                    />
                    <label className="form-label small mt-2">Account Number</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={invoiceData.bankAccount}
                      onChange={(e) => handleInvoiceChange("bankAccount", e.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small">Routing</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={invoiceData.bankRouting}
                      onChange={(e) => handleInvoiceChange("bankRouting", e.target.value)}
                    />
                    <label className="form-label small mt-2">SWIFT</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={invoiceData.bankSwift}
                      onChange={(e) => handleInvoiceChange("bankSwift", e.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small">bKash Number</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={invoiceData.bkashNumber}
                      onChange={(e) => handleInvoiceChange("bkashNumber", e.target.value)}
                    />
                    <label className="form-label small mt-2">Stripe Link</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={invoiceData.stripeLink}
                      onChange={(e) => handleInvoiceChange("stripeLink", e.target.value)}
                    />
                  </div>


                </div>

                <div className="row">
                  <div className="col-md-4">
                    <label className="form-label small">Interac e-Transfer Email</label>
                    <input
                      type="email"
                      className="form-control form-control-sm"
                      value={invoiceData.interacEmail}
                      onChange={(e) => handleInvoiceChange("interacEmail", e.target.value)}
                      placeholder="Enter email for Interac e-Transfer"
                    />
                    <small className="text-muted">Email address for Interac e-Transfer payments</small>
                  </div>
                </div>

                <hr />

                {/* Footer */}
                <div className="mb-2">
                  <label className="form-label small">Footer Text</label>
                  <input
                    type="text"
                    className="form-control"
                    value={invoiceData.footerText}
                    onChange={(e) => handleInvoiceChange("footerText", e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Invoice Preview */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title mb-3 text-center">Invoice Preview</h5>
                <div id="invoice-print" ref={invoiceRef}>
                  <div className="invoice" style={{ maxWidth: "1000px", margin: "0 auto", background: "#fff", borderRadius: "16px", overflow: "hidden", boxShadow: "0 20px 50px rgba(0,0,0,0.08)" }}>
                    <div
                      style={{
                        background: "linear-gradient(135deg, #1e3a8a, #2563eb)",
                        color: "white",
                        padding: "20px",
                        display: "flex",
                        justifyContent: "space-between", // important
                        alignItems: "flex-start",
                        flexWrap: "wrap", // responsive
                        gap: "16px"
                      }}
                    >
                      {/* LEFT SIDE */}
                      <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                        <div
                          style={{
                            width: "50px",
                            height: "50px",
                            borderRadius: "10px",
                            background: "rgba(255,255,255,0.2)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "8px",
                            border: "1px dashed rgba(255,255,255,0.6)"
                          }}
                        >
                          <img
                            src={logoImg}
                            alt="Qwik IT"
                            style={{
                              width: "100%",
                              height: "auto",
                              objectFit: "contain"
                            }}
                          />
                        </div>

                        <div>
                          <h1 style={{ margin: 0, fontSize: "24px", letterSpacing: "1px" }}>
                            INVOICE
                          </h1>
                          <p style={{ margin: "3px 0", fontSize: "12px", opacity: 0.9 }}>
                            Qwik IT Services
                          </p>
                        </div>
                      </div>

                      {/* RIGHT SIDE */}
                      <div style={{ textAlign: "right" }}>
                        <p style={{ margin: "4px 0", fontSize: "12px" }}>
                          <strong>Invoice No:</strong> {invoiceData.invoiceNo}
                        </p>
                        <p style={{ margin: "4px 0", fontSize: "12px" }}>
                          <strong>Date:</strong> {formatDate(invoiceData.invoiceDate)}
                        </p>
                       
                      </div>
                    </div>


                    <div style={{ padding: "20px" }}>
                      <div style={{ display: "flex", flexDirection: "row", gap: "16px", marginBottom: "20px" }}>
                        <div style={{ flex: 1, background: "#f9fafb", border: "1px solid #e5e7eb", padding: "16px", borderRadius: "12px", wordBreak: "break-word" }}>
                          <h3 style={{ marginTop: 0, marginBottom: "12px", color: "#1e3a8a", fontSize: "16px" }}>Bill To</h3>
                          <p style={{ margin: "6px 0", fontSize: "13px" }}><strong>{invoiceData.billToCompany || "-"}</strong></p>
                          <p style={{ margin: "6px 0", fontSize: "13px" }}>{invoiceData.billToAddress || "-"}</p>
                          <p style={{ margin: "6px 0", fontSize: "13px" }}>{invoiceData.billToCountry || "-"}</p>
                          <p style={{ margin: "6px 0", fontSize: "13px" }}>{invoiceData.billToPhone || "-"}</p>
                          <p style={{ margin: "6px 0", fontSize: "13px" }}>{invoiceData.billToEmail || "-"}</p>
                        </div>
                        <div style={{ flex: 1, background: "#f9fafb", border: "1px solid #e5e7eb", padding: "16px", borderRadius: "12px", wordBreak: "break-word" }}>
                          <h3 style={{ marginTop: 0, marginBottom: "12px", color: "#1e3a8a", fontSize: "16px" }}>Qwik IT Services</h3>
                          <p style={{ margin: "6px 0", fontSize: "13px" }}><strong>Head Office:</strong> {invoiceData.companyHeadOffice}</p>
                          <p style={{ margin: "6px 0", fontSize: "13px" }}><strong>Bangladesh Office:</strong> {invoiceData.companyBangladeshOffice}</p>
                          <p style={{ margin: "6px 0", fontSize: "13px" }}><strong>Phone:</strong> {invoiceData.companyPhoneBD}, {invoiceData.companyPhoneCanada}</p>
                          <p style={{ margin: "6px 0", fontSize: "13px" }}><strong>Support:</strong> {invoiceData.companySupportEmail}</p>
                          <p style={{ margin: "6px 0", fontSize: "13px" }}><strong>Billing:</strong> {invoiceData.companyBillingEmail}</p>
                          {/* <p style={{ margin: "6px 0", fontSize: "13px" }}><strong>Web:</strong> {invoiceData.companyWebsite}</p> */}
                        </div>
                      </div>

                      <div style={{ overflowX: "auto", margin: "16px 0", WebkitOverflowScrolling: "touch" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "500px" }}>
                          <thead>
                            <tr style={{ background: "#1e3a8a", color: "white" }}>
                              <th style={{ padding: "10px 12px", textAlign: "left", fontSize: "13px" }}>Project</th>
                              <th style={{ padding: "10px 12px", textAlign: "left", fontSize: "13px" }}>Description</th>
                              <th style={{ padding: "10px 12px", textAlign: "right", fontSize: "13px" }}>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {projectItems.map((item) => (
                              <tr key={item.id}>
                                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eee", fontSize: "13px", wordBreak: "break-word" }}>{item.projectName || "-"}</td>
                                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eee", fontSize: "13px", wordBreak: "break-word" }}>{item.description || "-"}</td>
                                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eee", textAlign: "right", fontSize: "13px", whiteSpace: "nowrap" }}>{formatCurrencyWithCode(item.amount)}</td>
                              </tr>
                            ))}
                            <tr style={{ fontWeight: "bold", background: "#f9fafb" }}>
                              <td colSpan="2" style={{ padding: "10px 12px", fontSize: "13px" }}><strong>Total</strong></td>
                              <td style={{ padding: "10px 12px", textAlign: "right", fontSize: "13px" }}><strong>{formatCurrencyWithCode(calculations.total)}</strong></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <div style={{ marginTop: "20px", background: "#f9fafb", padding: "16px", borderRadius: "12px", border: "1px solid #e5e7eb" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", flexWrap: "wrap", gap: "8px" }}>
                          <span>Total</span>
                          <span>{formatCurrencyWithCode(calculations.total)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", flexWrap: "wrap", gap: "8px" }}>
                          <span>Received</span>
                          <span style={{ color: "#16a34a", fontWeight: "bold" }}>{formatCurrencyWithCode(calculations.received)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", flexWrap: "wrap", gap: "8px" }}>
                          <span>Outstanding</span>
                          <span style={{ color: "#dc2626", fontWeight: "bold" }}>{formatCurrencyWithCode(calculations.outstanding)}</span>
                        </div>
                      </div>

                      <div style={{ marginTop: "24px", padding: "16px", borderRadius: "12px", background: "#fff7ed", borderLeft: "4px solid #f97316", textAlign: "center" }}>
                         <p>Minimum Due by: {formatDate(invoiceData.dueDate)}</p>
                        <h2 style={{ margin: "10px 0", color: "#dc2626", fontSize: "24px" }}>{formatCurrencyWithCode(paymentToCollect)}</h2>
                      </div>

                      <h3 style={{ marginTop: "24px", color: "#1e3a8a", fontSize: "16px" }}>Payment Methods</h3>

                      <div style={{ background: "#f8fafc", border: "1px solid #e5e7eb", padding: "14px", borderRadius: "12px", marginTop: "12px", wordBreak: "break-word" }}>
                        <h4 style={{ margin: "0 0 8px", color: "#1e3a8a", fontSize: "15px" }}>🏦 Bank Transfer</h4>
                        <p style={{ margin: "4px 0", fontSize: "13px" }}>{invoiceData.bankName}</p>
                        <p style={{ margin: "4px 0", fontSize: "13px" }}>Account: {invoiceData.bankAccount}</p>
                        <p style={{ margin: "4px 0", fontSize: "13px" }}>Routing: {invoiceData.bankRouting}</p>
                        <p style={{ margin: "4px 0", fontSize: "13px" }}>SWIFT: {invoiceData.bankSwift}</p>
                      </div>

                      <div style={{ background: "#f8fafc", border: "1px solid #e5e7eb", padding: "14px", borderRadius: "12px", marginTop: "12px", wordBreak: "break-word" }}>
                        <h4 style={{ margin: "0 0 8px", color: "#1e3a8a", fontSize: "15px" }}>📱 bKash</h4>
                        <p style={{ margin: "4px 0", fontSize: "13px" }}>{invoiceData.bkashNumber}</p>
                        <p style={{ margin: "4px 0", fontSize: "13px" }}>Send transaction ID after payment</p>
                      </div>

                      <div style={{ background: "#f8fafc", border: "1px solid #e5e7eb", padding: "14px", borderRadius: "12px", marginTop: "12px", wordBreak: "break-word" }}>
                        <h4 style={{ margin: "0 0 8px", color: "#1e3a8a", fontSize: "15px" }}>💳 Stripe</h4>
                        <p style={{ margin: "4px 0", fontSize: "13px" }}>{invoiceData.stripeLink}</p>
                      </div>

                      {invoiceData.interacEmail && invoiceData.interacEmail.trim() !== "" && (
                        <div style={{ background: "#f8fafc", border: "1px solid #e5e7eb", padding: "14px", borderRadius: "12px", marginTop: "12px", wordBreak: "break-word" }}>
                          <h4 style={{ margin: "0 0 8px", color: "#1e3a8a", fontSize: "15px" }}>🏦 Interac e-Transfer</h4>
                          <p style={{ margin: "4px 0", fontSize: "13px" }}>Send payment to: <strong>{invoiceData.interacEmail}</strong></p>

                        </div>
                      )}

                      <div style={{ marginTop: "24px", paddingTop: "16px", borderTop: "1px solid #e5e7eb", textAlign: "center", fontSize: "13px" }}>
                        <p style={{ margin: "4px 0" }}>Best regards,</p>
                        <p style={{ margin: "4px 0" }}><strong>Qwik IT Services</strong></p>
                        <p style={{ margin: "4px 0" }}>{invoiceData.companyPhoneCanada}</p>
                        <p style={{ margin: "4px 0" }}>{invoiceData.companyWebsite}</p>
                      </div>
                    </div>

                    <div style={{ textAlign: "center", padding: "20px", background: "#f3f4f6", fontSize: "12px", color: "#555" }}>
                      {invoiceData.footerText}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons Bottom */}
        <div className="row mt-4 mb-4 no-print">
          <div className="col-12">
            <div className="d-flex justify-content-center">
              <button
                type="button"
                className="btn btn-success"
                onClick={openSendModal}
                disabled={isSending}
              >
                <i className="fas fa-envelope me-1"></i>
                {isSending ? "Processing..." : "Send Invoice to Client"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for Send Options */}
      {showModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
        }}>
          <div style={{
            backgroundColor: "white",
            borderRadius: "12px",
            width: "90%",
            maxWidth: "400px",
            padding: "24px",
            position: "relative",
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          }}>
            {/* Close Button */}
            <button
              onClick={() => setShowModal(false)}
              style={{
                position: "absolute",
                top: "16px",
                right: "16px",
                background: "none",
                border: "none",
                fontSize: "20px",
                cursor: "pointer",
                color: "#666",
              }}
            >
              ✕
            </button>

            <h3 style={{ marginTop: 0, marginBottom: "20px", color: "#1e3a8a" }}>
              Send Invoice
            </h3>

            <p style={{ marginBottom: "24px", color: "#555" }}>
              How would you like to send this invoice?
            </p>

            <button
              onClick={handleSendOnly}
              disabled={isSending}
              style={{
                width: "100%",
                padding: "12px",
                marginBottom: "12px",
                backgroundColor: "#2563eb",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "bold",
                cursor: isSending ? "not-allowed" : "pointer",
                opacity: isSending ? 0.6 : 1,
              }}
            >
              {isSending ? "Sending..." : "Send Only"}
            </button>

            <button
              onClick={handleSaveAndSend}
              disabled={isSending}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: "#16a34a",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "bold",
                cursor: isSending ? "not-allowed" : "pointer",
                opacity: isSending ? 0.6 : 1,
              }}
            >
              {isSending ? "Saving & Sending..." : "Save & Send"}
            </button>

            <button
              onClick={() => setShowModal(false)}
              disabled={isSending}
              style={{
                width: "100%",
                padding: "12px",
                marginTop: "12px",
                backgroundColor: "#f3f4f6",
                color: "#374151",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "14px",
                cursor: isSending ? "not-allowed" : "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectInvoice;

// import React, { useState, useEffect, useRef } from "react";
// import { useParams, useNavigate, Link } from "react-router-dom";
// import { useSelector } from "react-redux";
// import { selectUser } from "store/features/auth/authSlice";
// import emailjs from "@emailjs/browser";
// import { toast } from "sonner";
// import Breadcrumb from "components/Common/Breadcrumb";
// import DashboardLoader from "components/Loader/DashboardLoader";
// import {
//   useGetProjectByIdQuery,
//   useGetClientQuery,
// } from "store/features/client/clientManagementApi";
// import logoImg from "../../assets/images/logo-sm.png";

// const ProjectInvoice = () => {
//   const { projectId } = useParams();
//   const navigate = useNavigate();
//   const user = useSelector(selectUser);
//   const invoiceRef = useRef(null);

//   const { data: project, isLoading: projectLoading } = useGetProjectByIdQuery(projectId);
//   const { data: client, isLoading: clientLoading } = useGetClientQuery(project?.client, {
//     skip: !project?.client,
//   });

//   const [isSending, setIsSending] = useState(false);

//   // Editable invoice state
//   const [invoiceData, setInvoiceData] = useState({
//     // Company Info (Left side - Qwik IT Services)
//     companyHeadOffice: "950 Hillcrest Rd, Saint John, NB E2N2B2",
//     companyBangladeshOffice: "House-752 Rd-8, Adabor, Dhaka 1207",
//     companySupportEmail: "support@qwikit.ca",
//     companyBillingEmail: "billing@qwikit.ca",
//     companyPhoneBD: "+880 1636-131313",
//     companyPhoneCanada: "+1 (506) 899-3939",
//     companyWebsite: "www.qwikit.ca",

//     // Bill To (Client Info)
//     billToCompany: "",
//     billToAddress: "",
//     billToCountry: "Bangladesh",
//     billToPhone: "",
//     billToEmail: "",

//     // Invoice Details
//     invoiceNo: "",
//     invoiceDate: new Date().toISOString().split("T")[0],
//     dueDate: "",

//     // Payment Methods
//     bankName: "City Bank PLC - Dhanmondi Branch",
//     bankAccount: "125 492 423 8001",
//     bankRouting: "225261187",
//     bankSwift: "CIBLBDDH",
//     bkashNumber: "01680001000",
//     stripeLink: "https://buy.stripe.com/00g17Be75cU3fWE3cd",

//     // Footer
//     footerText: "Thank you for your business with Qwik IT Services",
//   });

//   // Project items (editable)
//   const [projectItems, setProjectItems] = useState([
//     {
//       id: 1,
//       projectName: "",
//       description: "",
//       amount: 0,
//     },
//   ]);

//   // Calculations
//   const [calculations, setCalculations] = useState({
//     total: 0,
//     received: 0,
//     outstanding: 0,
//   });

//   // Load project data into invoice
//   useEffect(() => {
//     if (project && client) {
//       // Calculate total from project budget
//       const projectBudget = parseFloat(project.budget) || 0;

//       // Calculate total received from payments
//       const totalReceived = (project.payment || []).reduce(
//         (sum, payment) => sum + (parseFloat(payment.amount) || 0),
//         0
//       );

//       const outstandingAmount = projectBudget - totalReceived;

//       // Set initial project items from project data
//       const items = [
//         {
//           id: 1,
//           projectName: project.name || "",
//           description: project.description || "",
//           amount: projectBudget,
//         },
//       ];

//       setProjectItems(items);

//       // Set calculations
//       setCalculations({
//         total: projectBudget,
//         received: totalReceived,
//         outstanding: outstandingAmount,
//       });

//       // Get client email from client data
//       const clientEmail = client.client_email || client.email || "";

//       // Set invoice data with client info
//       setInvoiceData((prev) => ({
//         ...prev,
//         // Bill To (Client Info)
//         billToCompany: client.company_name || client.client_name || "",
//         billToAddress: client.address || "",
//         billToPhone: client.contact_number || "",
//         billToCountry: "Bangladesh",
//         billToEmail: clientEmail,

//         // Invoice details
//         invoiceNo: `QIT-INV-${project.id}-${Date.now().toString().slice(-6)}`,
//         invoiceDate: new Date().toISOString().split("T")[0],
//         dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
//       }));
//     }
//   }, [project, client]);

//   // Update calculations when items change
//   useEffect(() => {
//     const total = projectItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
//     const received = calculations.received;
//     const outstanding = total - received;

//     setCalculations((prev) => ({
//       ...prev,
//       total: total,
//       outstanding: outstanding,
//     }));
//   }, [projectItems]);

//   // Format currency
//   const formatCurrency = (amount) => {
//     return `BDT ${Math.round(amount).toLocaleString()}`;
//   };

//   // Handle invoice field changes
//   const handleInvoiceChange = (field, value) => {
//     setInvoiceData((prev) => ({ ...prev, [field]: value }));
//   };

//   // Handle project item changes
//   const handleProjectItemChange = (id, field, value) => {
//     setProjectItems((prev) =>
//       prev.map((item) =>
//         item.id === id ? { ...item, [field]: field === "amount" ? parseFloat(value) || 0 : value } : item
//       )
//     );
//   };

//   // Add new project item
//   const addProjectItem = () => {
//     const newId = Math.max(...projectItems.map((i) => i.id), 0) + 1;
//     setProjectItems([
//       ...projectItems,
//       {
//         id: newId,
//         projectName: "",
//         description: "",
//         amount: 0,
//       },
//     ]);
//   };

//   // Remove project item
//   const removeProjectItem = (id) => {
//     if (projectItems.length > 1) {
//       setProjectItems(projectItems.filter((item) => item.id !== id));
//     } else {
//       toast.error("At least one project item is required");
//     }
//   };

//   // Handle received amount change
//   const handleReceivedChange = (value) => {
//     const received = parseFloat(value) || 0;
//     setCalculations((prev) => ({
//       ...prev,
//       received: received,
//       outstanding: prev.total - received,
//     }));
//   };

//   // Generate responsive email HTML
//   const generateEmailHTML = () => {
//     const itemsRows = projectItems
//       .map(
//         (item) => `
//       <tr>
//         <td style="padding:12px;border-bottom:1px solid #eee;">${item.projectName || "-"}</td>
//         <td style="padding:12px;border-bottom:1px solid #eee;">${item.description || "-"}</td>
//         <td style="padding:12px;border-bottom:1px solid #eee;text-align:right;">${formatCurrency(item.amount)}</td>
//       </tr>
//     `
//       )
//       .join("");

//     return `
// <!DOCTYPE html>
// <html>
// <head>
// <meta charset="UTF-8">
// <title>Invoice</title>

//  <style>
//  @media only screen and (max-width:600px) {
//   .stack-column {
//     display: block !important;
//     width: 100% !important;
//     padding: 0 !important;
//   }

//    .stack-center {
//     display: block !important;
//     width: 100% !important;
//     text-align: center !important;
//     padding: 0 !important;
//     margin-bottom: 10px;
//   }

//   .stack-left {
//     display: block !important;
//     width: 100% !important;
//     text-align: left !important;
//     padding-top: 10px !important;
//   }

// }
//   </style>
// </head>

// <body style="margin:0;padding:0;background:#eef2f7;font-family:Arial, sans-serif;">

// <table width="100%" cellpadding="0" cellspacing="0" style="padding:16px;">
// <tr>
// <td align="center">

// <table width="100%" cellpadding="0" cellspacing="0" style="max-width:900px;background:#fff;border-radius:16px;overflow:hidden;">

// <!-- HEADER -->
// <!-- HEADER -->
// <tr>
// <td style="background:linear-gradient(135deg,#1e3a8a,#2563eb);color:white;padding:20px;">

//   <table width="100%" cellpadding="0" cellspacing="0">
//     <tr>

//       <!-- LEFT (LOGO) -->
//       <td class="stack-center" width="80" valign="middle" align="center">
//   <img
//     src="https://res.cloudinary.com/dlaeldpqy/image/upload/v1777306544/logo-sm_ubs6bx.png"
//     width="60"
//     style="display:block; margin:0 auto;">
// </td>

//       <!-- MIDDLE (TITLE) -->
//       <td class="stack-center" valign="middle" style="padding-left:10px;">
//         <h1 style="margin:0;font-size:24px;">INVOICE</h1>
//         <p style="margin:3px 0;font-size:12px;">Qwik IT Services</p>
//       </td>

//       <!-- RIGHT (INFO) -->
//       <td class="stack-left" align="right" valign="middle" style="font-size:12px;">
//         <p><strong>Invoice No:</strong> ${invoiceData.invoiceNo}</p>
//         <p><strong>Date:</strong> ${invoiceData.invoiceDate}</p>
//         <p><strong>Due Date:</strong> ${invoiceData.dueDate}</p>
//       </td>

//     </tr>
//   </table>

// </td>
// </tr>

// <!-- CONTENT -->
// <tr>
// <td style="padding:20px;">

// <!-- INFO GRID -->
// <!-- INFO GRID -->
// <table width="100%" cellpadding="0" cellspacing="0">
//   <tr>

//     <!-- LEFT -->
//     <td class="stack-column" width="50%" valign="top" style="padding-right:10px; margin-bottom:8px">
//       <table width="100%" style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:12px;">
//         <tr>
//           <td style="padding:16px;">
//             <h3 style="margin:0 0 10px;color:#1e3a8a;">Bill To</h3>
//             <p><strong>${invoiceData.billToCompany || "-"}</strong></p>
//             <p>${invoiceData.billToAddress || "-"}</p>
//             <p>${invoiceData.billToCountry || "-"}</p>
//             <p>${invoiceData.billToPhone || "-"}</p>
//             <p>${invoiceData.billToEmail || "-"}</p>
//           </td>
//         </tr>
//       </table>
//     </td>

//     <!-- RIGHT -->
//     <td class="stack-column" width="50%" valign="top" style="padding-left:10px;">
//       <table width="100%" style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:12px;">
//         <tr>
//           <td style="padding:16px;">
//             <h3 style="margin:0 0 10px;color:#1e3a8a;">Qwik IT Services</h3>
//             <p><strong>Head Office:</strong> ${invoiceData.companyHeadOffice}</p>
//             <p><strong>Bangladesh Office:</strong> ${invoiceData.companyBangladeshOffice}</p>
//             <p><strong>Support:</strong> ${invoiceData.companySupportEmail}</p>
//             <p><strong>Billing:</strong> ${invoiceData.companyBillingEmail}</p>
//             <p><strong>Phone:</strong> ${invoiceData.companyPhoneBD}, ${invoiceData.companyPhoneCanada}</p>
//             <p><strong>Web:</strong> ${invoiceData.companyWebsite}</p>
//           </td>
//         </tr>
//       </table>
//     </td>

//   </tr>
// </table>

// <!-- TABLE -->
// <table width="100%" cellpadding="0" cellspacing="0" style="margin-top:20px;border-collapse:collapse;">
// <tr style="background:#1e3a8a;color:white;">
// <th style="padding:10px;text-align:left;">Project</th>
// <th style="padding:10px;text-align:left;">Description</th>
// <th style="padding:10px;text-align:right;">Amount</th>
// </tr>

// ${itemsRows}

// <tr style="background:#f9fafb;font-weight:bold;">
// <td colspan="2" style="padding:10px;">Total</td>
// <td style="padding:10px;text-align:right;">${formatCurrency(calculations.total)}</td>
// </tr>
// </table>

// <!-- SUMMARY -->
// <table width="100%" style="margin-top:20px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:12px;">
// <tr>
// <td style="padding:10px;">Total</td>
// <td align="right" style="padding:10px;">${formatCurrency(calculations.total)}</td>
// </tr>
// <tr>
// <td style="padding:10px;">Received</td>
// <td align="right" style="padding:10px;color:green;">${formatCurrency(calculations.received)}</td>
// </tr>
// <tr>
// <td style="padding:10px;">Outstanding</td>
// <td align="right" style="padding:10px;color:red;">${formatCurrency(calculations.outstanding)}</td>
// </tr>
// </table>

// <!-- PAYMENT -->
// <table width="100%" style="margin-top:20px;background:#fff7ed;border-left:4px solid #f97316;text-align:center;">
// <tr>
// <td style="padding:16px;">
// <p>Outstanding Payment</p>
// <h2 style="color:red;">${formatCurrency(calculations.outstanding)}</h2>
// <p>Due Date: ${invoiceData.dueDate}</p>
// </td>
// </tr>
// </table>

// <!-- METHODS -->
// <h3 style="margin-top:20px;color:#1e3a8a;">Payment Methods</h3>

// <table width="100%" style="margin-top:10px;background:#f8fafc;border:1px solid #e5e7eb;border-radius:12px;">
// <tr><td style="padding:12px;">
// <p>

// <strong>🏦 Bank Transfer</strong><br>
// </p>
// <p>
// ${invoiceData.bankName}
// </p>
// <p>

// Account: ${invoiceData.bankAccount}<br>
// </p>
// <p>

// Routing: ${invoiceData.bankRouting}<br>
// </p>
// <p>

// SWIFT: ${invoiceData.bankSwift}
// </p>
// </td></tr>
// </table>

// <table width="100%" style="margin-top:10px;background:#f8fafc;border:1px solid #e5e7eb;border-radius:12px;">
// <tr><td style="padding:12px;">

// <p>

// <strong>📱 bKash</strong><br>
// </p>
// <p>

// ${invoiceData.bkashNumber}<br>
// </p>
// <p>

// Send transaction ID after payment
// </p>
// </td></tr>
// </table>

// <table width="100%" style="margin-top:10px;background:#f8fafc;border:1px solid #e5e7eb;border-radius:12px;">
// <tr><td style="padding:12px;">
// <p>

// <strong>💳 Stripe</strong><br>
// </p>
// <p>

// ${invoiceData.stripeLink}
// </p>
// </td></tr>
// </table>

// <!-- SIGNATURE -->
// <div style="margin-top:20px;text-align:center;font-size:13px;">
// <p>Best regards,</p>
// <p><strong>Qwik IT Services</strong></p>
// <p>${invoiceData.companyPhoneCanada}</p>
// <p>${invoiceData.companyWebsite}</p>
// </div>

// </td>
// </tr>

// <!-- FOOTER -->
// <tr>
// <td style="text-align:center;padding:20px;background:#f3f4f6;font-size:12px;">
// ${invoiceData.footerText}
// </td>
// </tr>

// </table>

// </td>
// </tr>
// </table>

// </body>
// </html>
// `;
//   };

//   // Send invoice via email
//   // const handleSendInvoice = async () => {
//   //   if (!invoiceData.billToCompany) {
//   //     toast.error("Please enter client company name");
//   //     return;
//   //   }

//   //   const recipientEmail = invoiceData.billToEmail || client?.client_email;

//   //   if (!recipientEmail) {
//   //     toast.error("Client email is required to send invoice");
//   //     return;
//   //   }

//   //   try {
//   //     setIsSending(true);
//   //     const emailHTML = generateEmailHTML();

//   //     const templateParams = {
//   //       to_name: invoiceData.billToCompany,
//   //       to_email: recipientEmail,
//   //       invoice_number: invoiceData.invoiceNo,
//   //       invoice_html: emailHTML,
//   //       total_amount: formatCurrency(calculations.outstanding),
//   //       company_name: "Qwik IT Services",
//   //     };

//   //     await emailjs.send(
//   //       "service_dlbg2mp",
//   //       "template_yeiboe9",
//   //       templateParams,
//   //       "ZTXs6yWR6FFrdIPMD"
//   //     );

//   //     toast.success(`Invoice #${invoiceData.invoiceNo} sent successfully to ${recipientEmail}`);
//   //   } catch (error) {
//   //     console.error("Error sending invoice:", error);
//   //     toast.error("Failed to send invoice. Please check EmailJS configuration.");
//   //   } finally {
//   //     setIsSending(false);
//   //   }
//   // };

//   const handleSendInvoice = async () => {
//     // Check if client company name exists
//     if (!invoiceData.billToCompany) {
//       toast.error("Please enter client company name");
//       return;
//     }

//     // Get client email
//     const recipientEmail = invoiceData.billToEmail || client?.client_email;

//     // Check if email exists
//     if (!recipientEmail) {
//       toast.error("Client email is required to send invoice");
//       return;
//     }

//     try {
//       setIsSending(true);

//       // Generate the invoice HTML
//       const emailHTML = generateEmailHTML();

//       // Send to your backend server (port 5000)
//       const response = await fetch('http://localhost:5000/api/send-invoice', {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify({
//           to: recipientEmail,
//           subject: `Invoice #${invoiceData.invoiceNo} - Qwik IT Services`,
//           html: emailHTML,
//           invoiceNo: invoiceData.invoiceNo,
//           clientName: invoiceData.billToCompany,
//         }),
//       });

//       const data = await response.json();

//       if (data.success) {
//         toast.success(`Invoice #${invoiceData.invoiceNo} sent successfully to ${recipientEmail}`);
//       } else {
//         throw new Error(data.error || 'Failed to send invoice');
//       }
//     } catch (error) {
//       console.error('Error sending invoice:', error);
//       toast.error('Failed to send invoice. Please make sure server is running on port 5000');
//     } finally {
//       setIsSending(false);
//     }
//   };


//   if (projectLoading || clientLoading) {
//     return <DashboardLoader />;
//   }

//   if (!project) {
//     return (
//       <div className="page-content">
//         <div className="container-fluid">
//           <div className="alert alert-danger">Project not found</div>
//           <Link to="/all-projects" className="btn btn-primary">
//             Back to Projects
//           </Link>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="page-content">
//       <div className="container-fluid">
//         <Breadcrumb title="Invoice" breadcrumbItem={`Invoice for ${project?.name || "Project"}`} />

//         {/* Action Buttons */}
//         <div className="row mb-4 no-print">
//           <div className="col-12">
//             <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
//               <Link to="/all-projects" className="btn btn-secondary">
//                 <i className="fas fa-arrow-left me-1"></i> Back to Projects
//               </Link>
//               <button
//                 type="button"
//                 className="btn btn-success"
//                 onClick={handleSendInvoice}
//                 disabled={isSending}
//               >
//                 <i className="fas fa-envelope me-1"></i>
//                 {isSending ? "Sending..." : "Send Invoice to Client"}
//               </button>
//             </div>
//           </div>
//         </div>

//         {/* Edit Form Section - Editable Fields */}
//         <div className="row mb-4 no-print">
//           <div className="col-12">
//             <div className="card">
//               <div className="card-body">
//                 <h5 className="card-title mb-3">Edit Invoice Details</h5>

//                 <div className="row">
//                   {/* Bill To Section */}
//                   <div className="col-md-6">
//                     <h6 className="mb-2 text-primary">Bill To (Client Information)</h6>
//                     <div className="mb-2">
//                       <label className="form-label small">Company Name</label>
//                       <input
//                         type="text"
//                         className="form-control form-control-sm"
//                         value={invoiceData.billToCompany}
//                         onChange={(e) => handleInvoiceChange("billToCompany", e.target.value)}
//                       />
//                     </div>
//                     <div className="mb-2">
//                       <label className="form-label small">Address</label>
//                       <input
//                         type="text"
//                         className="form-control form-control-sm"
//                         value={invoiceData.billToAddress}
//                         onChange={(e) => handleInvoiceChange("billToAddress", e.target.value)}
//                       />
//                     </div>
//                     <div className="mb-2">
//                       <label className="form-label small">Country</label>
//                       <input
//                         type="text"
//                         className="form-control form-control-sm"
//                         value={invoiceData.billToCountry}
//                         onChange={(e) => handleInvoiceChange("billToCountry", e.target.value)}
//                       />
//                     </div>
//                     <div className="mb-2">
//                       <label className="form-label small">Phone</label>
//                       <input
//                         type="text"
//                         className="form-control form-control-sm"
//                         value={invoiceData.billToPhone}
//                         onChange={(e) => handleInvoiceChange("billToPhone", e.target.value)}
//                       />
//                     </div>
//                     <div className="mb-2">
//                       <label className="form-label small">Email</label>
//                       <input
//                         type="email"
//                         className="form-control form-control-sm"
//                         value={invoiceData.billToEmail}
//                         onChange={(e) => handleInvoiceChange("billToEmail", e.target.value)}
//                       />
//                     </div>
//                   </div>

//                   {/* Invoice Details */}
//                   <div className="col-md-6">
//                     <h6 className="mb-2 text-primary">Invoice Details</h6>
//                     <div className="mb-2">
//                       <label className="form-label small">Invoice No</label>
//                       <input
//                         type="text"
//                         className="form-control form-control-sm"
//                         value={invoiceData.invoiceNo}
//                         onChange={(e) => handleInvoiceChange("invoiceNo", e.target.value)}
//                       />
//                     </div>
//                     <div className="mb-2">
//                       <label className="form-label small">Invoice Date</label>
//                       <input
//                         type="date"
//                         className="form-control form-control-sm"
//                         value={invoiceData.invoiceDate}
//                         onChange={(e) => handleInvoiceChange("invoiceDate", e.target.value)}
//                       />
//                     </div>
//                     <div className="mb-2">
//                       <label className="form-label small">Due Date</label>
//                       <input
//                         type="date"
//                         className="form-control form-control-sm"
//                         value={invoiceData.dueDate}
//                         onChange={(e) => handleInvoiceChange("dueDate", e.target.value)}
//                       />
//                     </div>
//                   </div>
//                 </div>

//                 <hr />

//                 {/* Project Items */}
//                 <h6 className="mb-2 text-primary">Project Items</h6>
//                 <div className="table-responsive">
//                   <table className="table table-bordered table-sm">
//                     <thead>
//                       <tr>
//                         <th>Project Name</th>
//                         <th>Description</th>
//                         <th style={{ width: "150px" }}>Amount (BDT)</th>
//                         <th style={{ width: "50px" }}></th>
//                       </tr>
//                     </thead>
//                     <tbody>
//                       {projectItems.map((item) => (
//                         <tr key={item.id}>
//                           <td>
//                             <input
//                               type="text"
//                               className="form-control form-control-sm"
//                               value={item.projectName}
//                               onChange={(e) => handleProjectItemChange(item.id, "projectName", e.target.value)}
//                             />
//                           </td>
//                           <td>
//                             <input
//                               type="text"
//                               className="form-control form-control-sm"
//                               value={item.description}
//                               onChange={(e) => handleProjectItemChange(item.id, "description", e.target.value)}
//                             />
//                           </td>
//                           <td>
//                             <input
//                               type="number"
//                               className="form-control form-control-sm"
//                               value={item.amount}
//                               onChange={(e) => handleProjectItemChange(item.id, "amount", e.target.value)}
//                             />
//                           </td>
//                           <td>
//                             <button
//                               type="button"
//                               className="btn btn-danger btn-sm"
//                               onClick={() => removeProjectItem(item.id)}
//                             >
//                               <i className="fas fa-trash"></i>
//                             </button>
//                           </td>
//                         </tr>
//                       ))}
//                     </tbody>
//                   </table>
//                 </div>

//                 <div className="d-flex justify-content-between align-items-center mt-2">
//                   <button type="button" className="btn btn-sm btn-primary" onClick={addProjectItem}>
//                     <i className="fas fa-plus me-1"></i> Add Item
//                   </button>
//                   <div className="text-end">
//                     <strong>Total: {formatCurrency(calculations.total)}</strong>
//                   </div>
//                 </div>

//                 <hr />

//                 {/* Payment Section */}
//                 <div className="row">
//                   <div className="col-md-4">
//                     <h6 className="mb-2 text-primary">Received Amount</h6>
//                     <input
//                       type="number"
//                       className="form-control"
//                       value={calculations.received}
//                       onChange={(e) => handleReceivedChange(e.target.value)}
//                       step="0.01"
//                     />
//                     <small className="text-muted">Amount already received from client</small>
//                   </div>
//                   <div className="col-md-4">
//                     <h6 className="mb-2 text-primary">Outstanding Amount</h6>
//                     <input
//                       type="text"
//                       className="form-control bg-light"
//                       value={formatCurrency(calculations.outstanding)}
//                       readOnly
//                     />
//                   </div>
//                 </div>

//                 <hr />

//                 {/* Company Info */}
//                 <h6 className="mb-2 text-primary">Company Information (Qwik IT Services)</h6>
//                 <div className="row">
//                   <div className="col-md-6">
//                     <div className="mb-2">
//                       <label className="form-label small">Head Office (Canada)</label>
//                       <input
//                         type="text"
//                         className="form-control form-control-sm"
//                         value={invoiceData.companyHeadOffice}
//                         onChange={(e) => handleInvoiceChange("companyHeadOffice", e.target.value)}
//                       />
//                     </div>
//                     <div className="mb-2">
//                       <label className="form-label small">Bangladesh Office</label>
//                       <input
//                         type="text"
//                         className="form-control form-control-sm"
//                         value={invoiceData.companyBangladeshOffice}
//                         onChange={(e) => handleInvoiceChange("companyBangladeshOffice", e.target.value)}
//                       />
//                     </div>
//                     <div className="mb-2">
//                       <label className="form-label small">Support Email</label>
//                       <input
//                         type="email"
//                         className="form-control form-control-sm"
//                         value={invoiceData.companySupportEmail}
//                         onChange={(e) => handleInvoiceChange("companySupportEmail", e.target.value)}
//                       />
//                     </div>
//                   </div>
//                   <div className="col-md-6">
//                     <div className="mb-2">
//                       <label className="form-label small">Billing Email</label>
//                       <input
//                         type="email"
//                         className="form-control form-control-sm"
//                         value={invoiceData.companyBillingEmail}
//                         onChange={(e) => handleInvoiceChange("companyBillingEmail", e.target.value)}
//                       />
//                     </div>
//                     <div className="mb-2">
//                       <label className="form-label small">Phone (BD)</label>
//                       <input
//                         type="text"
//                         className="form-control form-control-sm"
//                         value={invoiceData.companyPhoneBD}
//                         onChange={(e) => handleInvoiceChange("companyPhoneBD", e.target.value)}
//                       />
//                     </div>
//                     <div className="mb-2">
//                       <label className="form-label small">Phone (Canada)</label>
//                       <input
//                         type="text"
//                         className="form-control form-control-sm"
//                         value={invoiceData.companyPhoneCanada}
//                         onChange={(e) => handleInvoiceChange("companyPhoneCanada", e.target.value)}
//                       />
//                     </div>
//                     <div className="mb-2">
//                       <label className="form-label small">Website</label>
//                       <input
//                         type="text"
//                         className="form-control form-control-sm"
//                         value={invoiceData.companyWebsite}
//                         onChange={(e) => handleInvoiceChange("companyWebsite", e.target.value)}
//                       />
//                     </div>
//                   </div>
//                 </div>

//                 <hr />

//                 {/* Payment Methods */}
//                 <h6 className="mb-2 text-primary">Payment Methods</h6>
//                 <div className="row">
//                   <div className="col-md-4">
//                     <label className="form-label small">Bank Name</label>
//                     <input
//                       type="text"
//                       className="form-control form-control-sm"
//                       value={invoiceData.bankName}
//                       onChange={(e) => handleInvoiceChange("bankName", e.target.value)}
//                     />
//                     <label className="form-label small mt-2">Account Number</label>
//                     <input
//                       type="text"
//                       className="form-control form-control-sm"
//                       value={invoiceData.bankAccount}
//                       onChange={(e) => handleInvoiceChange("bankAccount", e.target.value)}
//                     />
//                   </div>
//                   <div className="col-md-4">
//                     <label className="form-label small">Routing</label>
//                     <input
//                       type="text"
//                       className="form-control form-control-sm"
//                       value={invoiceData.bankRouting}
//                       onChange={(e) => handleInvoiceChange("bankRouting", e.target.value)}
//                     />
//                     <label className="form-label small mt-2">SWIFT</label>
//                     <input
//                       type="text"
//                       className="form-control form-control-sm"
//                       value={invoiceData.bankSwift}
//                       onChange={(e) => handleInvoiceChange("bankSwift", e.target.value)}
//                     />
//                   </div>
//                   <div className="col-md-4">
//                     <label className="form-label small">bKash Number</label>
//                     <input
//                       type="text"
//                       className="form-control form-control-sm"
//                       value={invoiceData.bkashNumber}
//                       onChange={(e) => handleInvoiceChange("bkashNumber", e.target.value)}
//                     />
//                     <label className="form-label small mt-2">Stripe Link</label>
//                     <input
//                       type="text"
//                       className="form-control form-control-sm"
//                       value={invoiceData.stripeLink}
//                       onChange={(e) => handleInvoiceChange("stripeLink", e.target.value)}
//                     />
//                   </div>
//                 </div>

//                 <hr />

//                 {/* Footer */}
//                 <div className="mb-2">
//                   <label className="form-label small">Footer Text</label>
//                   <input
//                     type="text"
//                     className="form-control"
//                     value={invoiceData.footerText}
//                     onChange={(e) => handleInvoiceChange("footerText", e.target.value)}
//                   />
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Invoice Preview */}
//         <div className="row">
//           <div className="col-12">
//             <div className="card">
//               <div className="card-body">
//                 <h5 className="card-title mb-3 text-center">Invoice Preview</h5>
//                 <div id="invoice-print" ref={invoiceRef}>
//                   <div className="invoice" style={{ maxWidth: "1000px", margin: "0 auto", background: "#fff", borderRadius: "16px", overflow: "hidden", boxShadow: "0 20px 50px rgba(0,0,0,0.08)" }}>
//                     <div style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "white", padding: "20px", display: "flex", flexDirection: "column", gap: "16px" }}>
//                       <div style={{ display: "flex", alignItems: "center", gap: "15px", flexWrap: "wrap" }}>
//                         <div style={{ width: "50px", height: "50px", borderRadius: "10px", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", padding: "8px", border: "1px dashed rgba(255,255,255,0.6)" }}>
//                           <img src={logoImg} alt="Qwik IT" style={{ width: "100%", height: "auto", maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
//                         </div>
//                         <div>
//                           <h1 style={{ margin: 0, fontSize: "24px", letterSpacing: "1px" }}>INVOICE</h1>
//                           <p style={{ margin: "3px 0", fontSize: "12px", opacity: 0.9 }}>Qwik IT Services</p>
//                         </div>
//                       </div>
//                       <div style={{ textAlign: "left" }}>
//                         <p style={{ margin: "4px 0", fontSize: "12px" }}><strong>Invoice No:</strong> {invoiceData.invoiceNo}</p>
//                         <p style={{ margin: "4px 0", fontSize: "12px" }}><strong>Date:</strong> {invoiceData.invoiceDate}</p>
//                         <p style={{ margin: "4px 0", fontSize: "12px" }}><strong>Due Date:</strong> {invoiceData.dueDate}</p>
//                       </div>
//                     </div>

//                     <div style={{ padding: "20px" }}>
//                       <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "20px" }}>
//                         <div style={{ flex: 1, background: "#f9fafb", border: "1px solid #e5e7eb", padding: "16px", borderRadius: "12px", wordBreak: "break-word" }}>
//                           <h3 style={{ marginTop: 0, marginBottom: "12px", color: "#1e3a8a", fontSize: "16px" }}>Bill To</h3>
//                           <p style={{ margin: "6px 0", fontSize: "13px" }}><strong>{invoiceData.billToCompany || "-"}</strong></p>
//                           <p style={{ margin: "6px 0", fontSize: "13px" }}>{invoiceData.billToAddress || "-"}</p>
//                           <p style={{ margin: "6px 0", fontSize: "13px" }}>{invoiceData.billToCountry || "-"}</p>
//                           <p style={{ margin: "6px 0", fontSize: "13px" }}>{invoiceData.billToPhone || "-"}</p>
//                           <p style={{ margin: "6px 0", fontSize: "13px" }}>{invoiceData.billToEmail || "-"}</p>
//                         </div>
//                         <div style={{ flex: 1, background: "#f9fafb", border: "1px solid #e5e7eb", padding: "16px", borderRadius: "12px", wordBreak: "break-word" }}>
//                           <h3 style={{ marginTop: 0, marginBottom: "12px", color: "#1e3a8a", fontSize: "16px" }}>Qwik IT Services</h3>
//                           <p style={{ margin: "6px 0", fontSize: "13px" }}><strong>Head Office:</strong> {invoiceData.companyHeadOffice}</p>
//                           <p style={{ margin: "6px 0", fontSize: "13px" }}><strong>Bangladesh Office:</strong> {invoiceData.companyBangladeshOffice}</p>
//                           <p style={{ margin: "6px 0", fontSize: "13px" }}><strong>Support:</strong> {invoiceData.companySupportEmail}</p>
//                           <p style={{ margin: "6px 0", fontSize: "13px" }}><strong>Billing:</strong> {invoiceData.companyBillingEmail}</p>
//                           <p style={{ margin: "6px 0", fontSize: "13px" }}><strong>Phone:</strong> {invoiceData.companyPhoneBD}, {invoiceData.companyPhoneCanada}</p>
//                           <p style={{ margin: "6px 0", fontSize: "13px" }}><strong>Web:</strong> {invoiceData.companyWebsite}</p>
//                         </div>
//                       </div>

//                       <div style={{ overflowX: "auto", margin: "16px 0", WebkitOverflowScrolling: "touch" }}>
//                         <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "500px" }}>
//                           <thead>
//                             <tr style={{ background: "#1e3a8a", color: "white" }}>
//                               <th style={{ padding: "10px 12px", textAlign: "left", fontSize: "13px" }}>Project</th>
//                               <th style={{ padding: "10px 12px", textAlign: "left", fontSize: "13px" }}>Description</th>
//                               <th style={{ padding: "10px 12px", textAlign: "right", fontSize: "13px" }}>Amount</th>
//                             </tr>
//                           </thead>
//                           <tbody>
//                             {projectItems.map((item) => (
//                               <tr key={item.id}>
//                                 <td style={{ padding: "10px 12px", borderBottom: "1px solid #eee", fontSize: "13px", wordBreak: "break-word" }}>{item.projectName || "-"}</td>
//                                 <td style={{ padding: "10px 12px", borderBottom: "1px solid #eee", fontSize: "13px", wordBreak: "break-word" }}>{item.description || "-"}</td>
//                                 <td style={{ padding: "10px 12px", borderBottom: "1px solid #eee", textAlign: "right", fontSize: "13px", whiteSpace: "nowrap" }}>{formatCurrency(item.amount)}</td>
//                               </tr>
//                             ))}
//                             <tr style={{ fontWeight: "bold", background: "#f9fafb" }}>
//                               <td colSpan="2" style={{ padding: "10px 12px", fontSize: "13px" }}><strong>Total</strong></td>
//                               <td style={{ padding: "10px 12px", textAlign: "right", fontSize: "13px" }}><strong>{formatCurrency(calculations.total)}</strong></td>
//                             </tr>
//                           </tbody>
//                         </table>
//                       </div>

//                       <div style={{ marginTop: "20px", background: "#f9fafb", padding: "16px", borderRadius: "12px", border: "1px solid #e5e7eb" }}>
//                         <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", flexWrap: "wrap", gap: "8px" }}>
//                           <span>Total</span>
//                           <span>{formatCurrency(calculations.total)}</span>
//                         </div>
//                         <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", flexWrap: "wrap", gap: "8px" }}>
//                           <span>Received</span>
//                           <span style={{ color: "#16a34a", fontWeight: "bold" }}>{formatCurrency(calculations.received)}</span>
//                         </div>
//                         <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", flexWrap: "wrap", gap: "8px" }}>
//                           <span>Outstanding</span>
//                           <span style={{ color: "#dc2626", fontWeight: "bold" }}>{formatCurrency(calculations.outstanding)}</span>
//                         </div>
//                       </div>

//                       <div style={{ marginTop: "24px", padding: "16px", borderRadius: "12px", background: "#fff7ed", borderLeft: "4px solid #f97316", textAlign: "center" }}>
//                         <p>Outstanding Payment</p>
//                         <h2 style={{ margin: "10px 0", color: "#dc2626", fontSize: "24px" }}>{formatCurrency(calculations.outstanding)}</h2>
//                         <p>Due Date: {invoiceData.dueDate}</p>
//                       </div>

//                       <h3 style={{ marginTop: "24px", color: "#1e3a8a", fontSize: "16px" }}>Payment Methods</h3>

//                       <div style={{ background: "#f8fafc", border: "1px solid #e5e7eb", padding: "14px", borderRadius: "12px", marginTop: "12px", wordBreak: "break-word" }}>
//                         <h4 style={{ margin: "0 0 8px", color: "#1e3a8a", fontSize: "15px" }}>🏦 Bank Transfer</h4>
//                         <p style={{ margin: "4px 0", fontSize: "13px" }}>{invoiceData.bankName}</p>
//                         <p style={{ margin: "4px 0", fontSize: "13px" }}>Account: {invoiceData.bankAccount}</p>
//                         <p style={{ margin: "4px 0", fontSize: "13px" }}>Routing: {invoiceData.bankRouting}</p>
//                         <p style={{ margin: "4px 0", fontSize: "13px" }}>SWIFT: {invoiceData.bankSwift}</p>
//                       </div>

//                       <div style={{ background: "#f8fafc", border: "1px solid #e5e7eb", padding: "14px", borderRadius: "12px", marginTop: "12px", wordBreak: "break-word" }}>
//                         <h4 style={{ margin: "0 0 8px", color: "#1e3a8a", fontSize: "15px" }}>📱 bKash</h4>
//                         <p style={{ margin: "4px 0", fontSize: "13px" }}>{invoiceData.bkashNumber}</p>
//                         <p style={{ margin: "4px 0", fontSize: "13px" }}>Send transaction ID after payment</p>
//                       </div>

//                       <div style={{ background: "#f8fafc", border: "1px solid #e5e7eb", padding: "14px", borderRadius: "12px", marginTop: "12px", wordBreak: "break-word" }}>
//                         <h4 style={{ margin: "0 0 8px", color: "#1e3a8a", fontSize: "15px" }}>💳 Stripe</h4>
//                         <p style={{ margin: "4px 0", fontSize: "13px" }}>{invoiceData.stripeLink}</p>
//                       </div>

//                       <div style={{ marginTop: "24px", paddingTop: "16px", borderTop: "1px solid #e5e7eb", textAlign: "center", fontSize: "13px" }}>
//                         <p style={{ margin: "4px 0" }}>Best regards,</p>
//                         <p style={{ margin: "4px 0" }}><strong>Qwik IT Services</strong></p>
//                         <p style={{ margin: "4px 0" }}>{invoiceData.companyPhoneCanada}</p>
//                         <p style={{ margin: "4px 0" }}>{invoiceData.companyWebsite}</p>
//                       </div>
//                     </div>

//                     <div style={{ textAlign: "center", padding: "20px", background: "#f3f4f6", fontSize: "12px", color: "#555" }}>
//                       {invoiceData.footerText}
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Action Buttons Bottom */}
//         <div className="row mt-4 mb-4 no-print">
//           <div className="col-12">
//             <div className="d-flex justify-content-center">
//               <button
//                 type="button"
//                 className="btn btn-success"
//                 onClick={handleSendInvoice}
//                 disabled={isSending}
//               >
//                 <i className="fas fa-envelope me-1"></i>
//                 {isSending ? "Sending..." : "Send Invoice to Client"}
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ProjectInvoice;
